/**
 * The API guide tab of Integrations: how an internal system calls the integration API. It is prose around the rules
 * the server enforces (``app/api/api_keys.py``); the operations themselves are on the API reference tab, built from
 * the API's own contract.
 */

import { CopyButton } from '@/ui/CopyButton';

function Snippet({ id, label, text }: { id: string; label: string; text: string }) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <textarea id={id} className="token-snippet" readOnly rows={text.split('\n').length} value={text} />
      <div>
        <CopyButton text={text} label={`Copy ${label.toLowerCase()}`} />
      </div>
    </div>
  );
}

export function curlExample(origin: string): string {
  return `curl -H "Authorization: Bearer $WYNXX_API_KEY" "${origin}/api/assets?q=reconciliation"`;
}

export function pythonExample(origin: string): string {
  return [
    'import os, requests',
    '',
    `api = requests.Session()`,
    `api.headers["Authorization"] = f"Bearer {os.environ['WYNXX_API_KEY']}"`,
    `asset = api.get("${origin}/api/assets/<asset_id>").json()`,
    `answer = api.patch("${origin}/api/assets/<asset_id>", json={"revision": asset["revision"], "tagline": "New tagline"})`,
    'answer.raise_for_status()',
  ].join('\n');
}

export function ApiGuideTab() {
  const origin = window.location.origin;
  return (
    <div className="markdown stack-md">
      <section className="stack-sm" aria-labelledby="guide-auth">
        <h2 id="guide-auth" className="config-subhead">Authenticate with an API key</h2>
        <p>
          Create a key on the API keys tab and tick only what the system needs. Send it on every request as{' '}
          <code>Authorization: Bearer wynxx_api_…</code>. The system then acts as you, within those permissions: it never
          reaches more than you could do yourself. A key lasts up to 90 days; extend it before it ends.
        </p>
        <Snippet id="guide-curl" label="curl" text={curlExample(origin)} />
        <Snippet id="guide-python" label="Python" text={pythonExample(origin)} />
      </section>
      <section className="stack-sm" aria-labelledby="guide-rules">
        <h2 id="guide-rules" className="config-subhead">Rules every call follows</h2>
        <ul>
          <li>
            The base address is <code>{origin}/api</code>. Only the operations on the API reference tab take a key; any
            other address answers <code>401</code>.
          </li>
          <li>
            Send the key alone: a request that also carries a browser session is refused (<code>400</code>). No other
            header is needed.
          </li>
          <li>
            A change to an asset sends the <code>revision</code> it read. If someone saved since, the answer is{' '}
            <code>409</code>: read the asset again and decide.
          </li>
          <li>
            Errors answer <code>{'{"detail": …}'}</code>; a <code>422</code> lists each field that does not follow the
            rules and why.
          </li>
          <li>
            Up to 120 requests a minute per key, then <code>429</code>. Changes can be switched off for the whole
            environment; they then answer <code>403</code>.
          </li>
          <li>
            Nothing here runs AI: submitting or researching ideas, creating assets from files and the Advisor stay in
            the portal.
          </li>
        </ul>
      </section>
      <section className="stack-sm" aria-labelledby="guide-versions">
        <h2 id="guide-versions" className="config-subhead">Versions</h2>
        <p>
          Each operation keeps its <code>operationId</code>. A change to the integration API is announced in What’s new
          before it ships, and the reference always shows the version that runs.
        </p>
      </section>
    </div>
  );
}
